import { useState } from "react";
import { Play, FileText, Music, Images } from "lucide-react";
import { VideoPlayer } from "@/components/video-player";
import { formatDuration } from "@/lib/media";
import { cn } from "@/lib/utils";
import { MediaLightbox } from "@/components/media-lightbox";
import { optimizedImage, imageSrcSet } from "@/lib/image-url";
import type { PostMedia } from "@/components/post-card";
import { useAuth } from "@/lib/auth-context";
import { canWatch, normalizeReviewState, useIsMediaReviewer } from "@/lib/media-review";
import { MediaReviewBadge, MediaReviewPlaceholder } from "@/components/media-review-badge";
import {
  PHOTO_MAX_RATIO, PHOTO_MIN_RATIO, VIDEO_MAX_RATIO, VIDEO_MIN_RATIO,
  clampRatio, useNaturalRatio,
} from "@/lib/media-ratio";

/**
 * The one media surface for a post anywhere in Xamseen.
 *
 * The rules it enforces, so no upload ever takes over the page:
 * - one video: dominant, but shown at its own aspect ratio, never cropped
 * - one photo: a controlled frame, capped in height, tap to open full size
 * - two photos: equal side-by-side pair
 * - three photos: one lead image with two supporting ones
 * - four or more: a compact grid with a "+N" overlay
 * - video plus photos: the video leads, photos become a small supporting row
 * Audio and documents never enter the visual composition; they list underneath.
 */
export function MediaGallery({ media, ownerId }: { media: PostMedia[]; ownerId?: string | null }) {
  const { user } = useAuth();
  const isReviewer = useIsMediaReviewer();
  const isOwner = !!user && !!ownerId && user.id === ownerId;

  const sorted = [...media].sort((a, b) => a.position - b.position);
  const visuals = sorted.filter((m) => m.media_type === "image" || m.media_type === "video");
  const files = sorted.filter((m) => m.media_type === "audio" || m.media_type === "document");

  const [open, setOpen] = useState(false);
  const [startIdx, setStartIdx] = useState(0);
  const openAt = (i: number) => { setStartIdx(i); setOpen(true); };

  const perms = { isOwner, isReviewer };
  const leadVideoIdx = visuals.findIndex((m) => m.media_type === "video");
  const hasVideo = leadVideoIdx >= 0;

  if (visuals.length === 0 && files.length === 0) return null;

  let composition: React.ReactNode = null;

  if (visuals.length === 1) {
    const only = visuals[0]!;
    composition = only.media_type === "video"
      ? <VideoFrame media={only} perms={perms} />
      : <SoloPhoto media={only} onOpen={() => openAt(0)} />;
  } else if (hasVideo) {
    // Mixed: the clip leads, everything else becomes a supporting strip.
    const lead = visuals[leadVideoIdx]!;
    const rest = visuals.filter((_, i) => i !== leadVideoIdx);
    composition = (
      <div className="space-y-1.5">
        <VideoFrame media={lead} perms={perms} />
        <SupportRow items={rest} all={visuals} perms={perms} onOpen={openAt} />
      </div>
    );
  } else {
    composition = <PhotoGrid items={visuals} onOpen={openAt} />;
  }

  return (
    <>
      <div className="mt-3 space-y-2">
        {composition}
        {files.length > 0 && (
          <ul className="space-y-1.5">
            {files.map((f) => <li key={f.id}><FileRow media={f} /></li>)}
          </ul>
        )}
      </div>
      <MediaLightbox media={visuals} startIndex={startIdx} open={open} onOpenChange={setOpen} ownerId={ownerId} />
    </>
  );
}

type Perms = { isOwner: boolean; isReviewer: boolean };

const frame = "relative overflow-hidden rounded-lg border border-border bg-muted";

/* ---------------------------------------------------------------- single photo */

function SoloPhoto({ media, onOpen }: { media: PostMedia; onOpen: () => void }) {
  const natural = useNaturalRatio(media.url);
  const ratio = clampRatio(natural ?? 4 / 5, PHOTO_MIN_RATIO, PHOTO_MAX_RATIO);
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpen(); }}
      aria-label="Open photo"
      className={cn(frame, "group/tile block w-full max-h-[70svh]")}
      style={{ aspectRatio: String(ratio) }}
    >
      <Photo url={media.url} width={1000} widths={[600, 900, 1200]} sizes="(max-width: 640px) 100vw, 620px" />
    </button>
  );
}

/* ---------------------------------------------------------------------- video */

function VideoFrame({ media, perms }: { media: PostMedia; perms: Perms }) {
  const review = normalizeReviewState(media.review_state);
  const watchable = canWatch(review, perms);
  // A poster frame shares the clip's shape, so it is the cheapest ratio hint.
  const posterRatio = useNaturalRatio(media.poster_url ?? null);
  const ratio = clampRatio(posterRatio ?? 16 / 9, VIDEO_MIN_RATIO, VIDEO_MAX_RATIO);

  return (
    <div
      className={cn(frame, "w-full max-h-[78svh] bg-black")}
      style={{ aspectRatio: String(ratio) }}
    >
      {watchable ? (
        <VideoPlayer
          source={{ id: media.id, url: media.url, posterUrl: media.poster_url, durationSeconds: media.duration_seconds, scope: "post" }}
          objectFit="contain"
          autoPlayMuted
          loop
        />
      ) : (
        <MediaReviewPlaceholder state={review} />
      )}
      {review !== "approved" && (
        <span className="absolute right-2 top-2 z-10">
          <MediaReviewBadge state={review} scope="post" id={media.id} compact />
        </span>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ photo groupings */

function PhotoGrid({ items, onOpen }: { items: PostMedia[]; onOpen: (i: number) => void }) {
  const shown = items.slice(0, 4);
  const extra = items.length - shown.length;

  if (shown.length === 2) {
    return (
      <div className="grid grid-cols-2 gap-1.5">
        {shown.map((m, i) => (
          <Tile key={m.id} media={m} onOpen={() => onOpen(i)} ratio="4 / 5" />
        ))}
      </div>
    );
  }

  if (shown.length === 3) {
    return (
      <div className="grid aspect-[16/11] grid-cols-3 grid-rows-2 gap-1.5">
        <Tile media={shown[0]!} onOpen={() => onOpen(0)} className="col-span-2 row-span-2" fill />
        <Tile media={shown[1]!} onOpen={() => onOpen(1)} fill />
        <Tile media={shown[2]!} onOpen={() => onOpen(2)} fill />
      </div>
    );
  }

  return (
    <div className="grid aspect-square grid-cols-2 grid-rows-2 gap-1.5">
      {shown.map((m, i) => (
        <Tile
          key={m.id}
          media={m}
          onOpen={() => onOpen(i)}
          fill
          overlay={i === shown.length - 1 && extra > 0 ? extra : undefined}
        />
      ))}
    </div>
  );
}

/** Compact supporting row beside a lead video. */
function SupportRow({
  items, all, perms, onOpen,
}: { items: PostMedia[]; all: PostMedia[]; perms: Perms; onOpen: (i: number) => void }) {
  const shown = items.slice(0, 3);
  const extra = items.length - shown.length;
  return (
    <div className="grid grid-cols-3 gap-1.5">
      {shown.map((m, i) => (
        <Tile
          key={m.id}
          media={m}
          perms={perms}
          onOpen={() => onOpen(all.indexOf(m))}
          ratio="1 / 1"
          overlay={i === shown.length - 1 && extra > 0 ? extra : undefined}
        />
      ))}
    </div>
  );
}

function Tile({
  media, onOpen, className, ratio, fill, overlay, perms,
}: {
  media: PostMedia;
  onOpen: () => void;
  className?: string;
  ratio?: string;
  fill?: boolean;
  overlay?: number;
  perms?: Perms;
}) {
  const isVideo = media.media_type === "video";
  const review = normalizeReviewState(media.review_state);
  const blocked = isVideo && perms && !canWatch(review, perms);

  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpen(); }}
      aria-label={isVideo ? "Open video" : "Open photo"}
      className={cn(frame, "group/tile block text-left", fill && "h-full w-full", className)}
      style={ratio ? { aspectRatio: ratio } : undefined}
    >
      {blocked ? (
        <MediaReviewPlaceholder state={review} />
      ) : isVideo ? (
        <div className="relative h-full w-full bg-black">
          {media.poster_url ? (
            <img src={media.poster_url} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
          ) : null}
          <span className="pointer-events-none absolute inset-0 grid place-items-center">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-black/65 text-white">
              <Play className="h-4 w-4 fill-current" />
            </span>
          </span>
          {media.duration_seconds ? (
            <span className="pointer-events-none absolute bottom-1.5 right-1.5 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-white">
              {formatDuration(media.duration_seconds)}
            </span>
          ) : null}
        </div>
      ) : (
        <Photo url={media.url} width={620} widths={[320, 500, 700]} sizes="(max-width: 640px) 50vw, 310px" />
      )}
      {overlay ? (
        <span className="pointer-events-none absolute inset-0 grid place-items-center gap-1 bg-black/60 text-white">
          <span className="flex items-center gap-1.5 text-lg font-semibold">
            <Images className="h-4 w-4" aria-hidden />+{overlay}
          </span>
        </span>
      ) : null}
    </button>
  );
}

function Photo({ url, width, widths, sizes }: { url: string; width: number; widths: number[]; sizes: string }) {
  return (
    <img
      src={optimizedImage(url, width)}
      srcSet={imageSrcSet(url, widths)}
      sizes={sizes}
      alt=""
      loading="lazy"
      decoding="async"
      fetchPriority="low"
      className="h-full w-full object-cover transition-transform duration-300 group-hover/tile:scale-[1.02]"
    />
  );
}

/* --------------------------------------------------------- audio + documents */

function FileRow({ media }: { media: PostMedia }) {
  if (media.media_type === "audio") {
    return (
      <div className="flex items-center gap-2.5 rounded-lg border border-border bg-card px-3 py-2.5">
        <Music className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <audio src={media.url} controls preload="none" className="h-9 w-full min-w-0" />
      </div>
    );
  }
  return (
    <a
      href={media.url}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="flex min-h-11 items-center gap-2.5 rounded-lg border border-border bg-card px-3 py-2.5 text-sm hover:bg-accent"
    >
      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <span className="truncate">Attached document</span>
    </a>
  );
}
